import * as React from 'react';

import { i18n } from '@ciao/shared';
import { WBCard, WBLink } from '@/core';

import { DeskLabel, DeskSlots, Reservation, Selector } from './components';
import { SlotSelectionProps, FloorType, DeskType, DesksType } from './model';

export type { SlotSelectionProps };

/**
 * SlotSelection is an UI element to allow the user to make a desk selection, by showing rows of available desks
 *  and showing reserved timeslots for those desks.
 *
 * Example usecases:
 * - Used on the timeslot-enabled 'create reservation' screen to select an available time slot / desk.
 *
 * @example
 * <WBSlotSelection desks={...} start='10:00' end='11:00' onChange={...} />
 *
 */

// These values are used for calculation of where reservations need to be shown on the timeslot chart.
const TIMESLOT_HOUR_START = 6; // Timeslot chart starts at 06:00

interface State {
	selectedDesk: number | null;
}

export class WBSlotSelection extends React.Component<SlotSelectionProps, State> {
	static defaultProps: Partial<SlotSelectionProps>;

	constructor(props: SlotSelectionProps) {
		super(props);

		this.state = {
			selectedDesk: null,
		};

		this.onDeskSelect = this.onDeskSelect.bind(this);
		this.renderDeskLabel = this.renderDeskLabel.bind(this);
		this.renderDeskSlots = this.renderDeskSlots.bind(this);
	}

	/**
	 * Only returns desks that
	 * - have no reservations
	 * - have reservations with a startAt and endAt times, considering they are considered full-day reservations if a reservation
	 *      has no times defined. In this case it should not be selectable.
	 *
	 * @param {array} desks array of desk objects
	 */
	getDesksWithNoOrValidReservations(desks: DeskType[]) {
		if (!desks || desks.length === 0) {
			return [];
		}

		const filteredDesks = desks.filter((desk) => {
			return (
				!desk.reservations ||
				desk.reservations.length === 0 ||
				desk.reservations.filter((reservation) => reservation.startAt && reservation.endAt).length > 0
			);
		});

		return filteredDesks;
	}

	/**
	 * Takes the data retrieved by /slots/desks and turns it into a flat array to be used by the render function
	 */
	getSlotData(deskData: DesksType[]) {
		const slotData = [];

		deskData.map(
			(office) =>
				office.floors &&
				office.floors.map((floor) => {
					floor.zones &&
						floor.zones.map((zone) => {
							zone.desks = this.getDesksWithNoOrValidReservations(zone.desks);

							slotData.push({
								floorplanUrl: floor.floorplanUrl,
								id: `slot-${office.id}-${floor.id}-${zone.id}`,
								items: zone.desks,
								subtitle: `${floor.name}, ${zone.name}`,
								title: office.name,
							});
						});
				}),
		);

		return slotData;
	}

	onDeskSelect(id: number) {
		const { onChange } = this.props;

		this.setState({ selectedDesk: id }, () => onChange && onChange(id));
	}

	render() {
		const { desks, onFloorplanPress } = this.props;
		const slotData = this.getSlotData(desks);

		return slotData.map((item: FloorType) => {
			return (
				<WBCard
					key={item.id}
					style={{ marginBottom: 15 }}
					subtitle={item.subtitle}
					title={item.title}
					titleEnd={
						item.floorplanUrl && !!onFloorplanPress ? (
							<WBLink onPress={() => onFloorplanPress(item.floorplanUrl)}>
								{i18n.t('screens.reservations.floorplan')}
							</WBLink>
						) : null
					}
				>
					<Selector
						item={item}
						renderDeskLabel={this.renderDeskLabel}
						renderDeskSlots={this.renderDeskSlots}
					/>
				</WBCard>
			);
		});
	}

	renderDeskLabel(desk: DeskType) {
		const { selectedDesk } = this.state;

		return <DeskLabel desk={desk} onDeskSelect={this.onDeskSelect} selectedDesk={selectedDesk} key={desk.id} />;
	}

	/**
	 * Renders a single desk row, containing all slot columns, where each column represents a half hour slot of the available
	 *  office day. The columns alternate between a 'regular' column as well as a 'wholeHour' slot, where the latter adds a
	 *  bit of styling to the top of the column (protrusive border) to make the time slot table a bit more readable.
	 */
	renderDeskSlots(desk: DeskType) {
		const { start, end } = this.props;
		const { selectedDesk } = this.state;

		return (
			<DeskSlots
				desk={desk}
				end={end}
				onDeskSelect={this.onDeskSelect}
				renderReservation={this.renderReservation}
				selectedDesk={selectedDesk}
				start={start}
				key={desk.id}
			/>
		);
	}

	/**
	 * Renders an absolutely positioned block that renders within the time slot columns, to show the user's prefered time slot
	 *  and time slots blocked by other reservations, depending on the given <type>.
	 * In the case there is no start or end time, or a checkout time is given, the reservation will not render.
	 *
	 * @param {string} start start time of the reservation block, expects 'hh:mm'.
	 * @param {string} end end time of the reservation block, expects 'hh:mm'.
	 * @param {string} checkoutAt reservation checkout time. Expects 'null' if no checkout has been done yet.
	 * @param {string} type reservation type, which determines the display. Expects 'own' (for prefered reservation) or 'blocked' (for time slots blocked by other reservations).
	 * @param {string} key unique key, used by react for render performance reasons.
	 */
	renderReservation(start: string, end: string, checkoutAt: string, type: string, key: string) {
		if (!start || !end || checkoutAt !== null) {
			return null;
		}

		const startArray = start.split(':');
		const endArray = end.split(':');

		// Calculate the start and end in hours (13:15 will be 13.25, 08:45 will be 8.75)
		const reservationStartInHours = parseInt(startArray[0]) + parseInt(startArray[1]) / 60;
		const reservationEndInHours = parseInt(endArray[0]) + parseInt(endArray[1]) / 60;

		// reservation start location = (<reservation start in hours> - <start time of timeslot graph>) * <timeslot column width>
		const reservationStart = (reservationStartInHours - TIMESLOT_HOUR_START) * 26;

		// reservation width = (<reservation end in hours> - <reservation start in hours>) * <timeslot column width> + <border width>
		const reservationWidth = (reservationEndInHours - reservationStartInHours) * 26 + 1;

		return (
			<Reservation
				key={key}
				reservationStart={reservationStart}
				reservationWidth={reservationWidth}
				type={type}
			/>
		);
	}
}

WBSlotSelection.defaultProps = {
	onChange: () => {},
};
